import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { Moment } from '@e3dc-react/shell/Libs/moment'
import { useContext, useMemo } from 'react'

import { getBatterySavings, getBatterySavingsBulk, getWallboxSavings, getWallboxSavingsBulk } from '../api/three-sixty-savings-difference-values.api'
import { BatterySavingsModel, WallboxSavingsModel } from '../../system-data-handler/types'
import { DynamicPowerPricesEnabledContext } from 'src/Libs/dynamic-power-prices/context/dynamic-power-prices-enabled-context'
import { ThreeSixtySavingsResolution } from '../types'
import { AuthFetchError } from '@e3dc-react/shell/Types'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'
import { useWallboxHardwareByStorage } from 'src/Libs/wallboxes/hooks'
import { useFarmContext } from 'src/Elements/Farm'
import { ApiResult, usePortalQueries } from 'src/Libs/api'

export type ThreeSixtySavingsTotalConsumption = {
  ThreeSixtySavings: {
    history: number
    isLoading?: boolean
  }
} | null

type UseThreeSixtySavingsTotalConsumptionProps = {
  storagesIds: string[]
  from?: Moment
  to?: Moment
  resolution?: ThreeSixtySavingsResolution
}

/**
 * Hook to fetch the total three sixty based consumption (wallbox and battery)
 */
export const useThreeSixtySavingsConsumption = ({
  storagesIds,
  from,
  to,
  resolution,
}: UseThreeSixtySavingsTotalConsumptionProps): ThreeSixtySavingsTotalConsumption => {
  const authFetch = useAuthFetchWithResult()

  const { isDynamicPowerPriceEnabled } = useContext(DynamicPowerPricesEnabledContext)
  const { data: wallboxes } = useWallboxHardwareByStorage()
  const { isFarm } = useFarmContext()
  const {
    featureFlags: { isNewEmobiltyRouteEnabled },
    isFeatureFlagsLoading,
  } = useFeatureFlagsContext()
  const shouldUseNewRoute = !isFeatureFlagsLoading && isNewEmobiltyRouteEnabled

  const wallboxSavingsQueryFn = async (): Promise<ApiResult<WallboxSavingsModel, AuthFetchError | Error>> => {
    if (isFarm) return getWallboxSavingsBulk(authFetch, storagesIds, shouldUseNewRoute, from?.valueOf(), to?.valueOf(), resolution)
    return getWallboxSavings(authFetch, shouldUseNewRoute, storagesIds[0], from?.valueOf(), to?.valueOf(), resolution)
  }
  const batterySavingsQueryFn = async (): Promise<ApiResult<BatterySavingsModel, AuthFetchError | Error>> => {
    if (isFarm) return getBatterySavingsBulk(authFetch, storagesIds ?? [], from?.valueOf(), to?.valueOf(), resolution)
    return getBatterySavings(authFetch, storagesIds[0], from?.valueOf(), to?.valueOf(), resolution)
  }
  const [batteryQuery, wallboxQuery] = usePortalQueries<BatterySavingsModel | WallboxSavingsModel>([
    {
      queryKey: ['three-sixty-savings-battery', storagesIds, from, to, resolution],
      queryFn: batterySavingsQueryFn,
      enabled: isDynamicPowerPriceEnabled && storagesIds.length > 0,
    },
    {
      queryKey: ['three-sixty-savings-wallbox', storagesIds, from, to, resolution],
      queryFn: wallboxSavingsQueryFn,
      enabled: isDynamicPowerPriceEnabled && storagesIds.length > 0 && wallboxes.length > 0,
    },
  ])
  const batteryData = batteryQuery.data as BatterySavingsModel | null | undefined
  const isBatteryLoading = batteryQuery.isLoading
  const wallboxData = wallboxQuery.data as WallboxSavingsModel | null | undefined
  const isWallboxLoading = wallboxQuery.isLoading

  return useMemo(() => {
    if (!isDynamicPowerPriceEnabled) return null
    const history = (batteryData?.consumedPower ?? 0) + (wallboxData?.consumedPower ?? 0)
    return { ThreeSixtySavings: { history, isLoading: isBatteryLoading || isWallboxLoading } }
  }, [isDynamicPowerPriceEnabled, wallboxData?.consumedPower, batteryData?.consumedPower, isBatteryLoading, isWallboxLoading])
}
